import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateRangeFilter, DateRangeFilterChange } from "@/components/filters/DateRangeFilter";
import { DateRangeChange } from "@/types";
import { calculateDateRange } from "@/utils/dateRangeUtils";
import { getSharedDateRange, setSharedDateRange } from "@/utils/generalDateRangeStore";
import {
  CallQualityDetailWidget,
  CallQualityDetailsModal,
  CallQualityMetricsSection,
  type CallQualityDetailRow,
} from "@/components/widgets/call-overview";
import {
  fetchCallQualityDistribution,
  fetchCallQualityMetrics,
} from "@/lib/axios/api/call-quality.api";
import type {
  CallQualityScorecardResponse,
} from "@/lib/axios/api/call-quality.api";

interface CQDTabProps {
  isGeneralMode: boolean;
}

const CQDTab: React.FC<CQDTabProps> = ({ isGeneralMode }) => {
  const [dateRange, setDateRange] = useState<DateRangeFilterChange>(getSharedDateRange);
  const [scorecardPayload, setScorecardPayload] = useState<CallQualityScorecardResponse | null>(null);
  const [scorecardLoading, setScorecardLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<CallQualityDetailRow | null>(null);
  const [callMicrosoftIdsFilter, setCallMicrosoftIdsFilter] = useState<string[] | undefined>(undefined);
  const [qualityLevelFilter, setQualityLevelFilter] = useState<"Good" | "Poor" | "Unclassified" | undefined>(undefined);
  const [failureTypeContext, setFailureTypeContext] = useState<"setup" | "drop" | undefined>(undefined);
  const callQualityDetailRef = useRef<HTMLDivElement>(null);
  const callQualityDetailDateParams = useMemo(
    () => ({
      startDate: dateRange.startDate.toISOString(),
      endDate: dateRange.endDate.toISOString(),
      previousStartDate: dateRange.previousStartDate?.toISOString(),
      previousEndDate: dateRange.previousEndDate?.toISOString(),
      callMicrosoftIds: callMicrosoftIdsFilter,
    }),
    [
      dateRange.startDate,
      dateRange.endDate,
      dateRange.previousStartDate,
      dateRange.previousEndDate,
      callMicrosoftIdsFilter,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const startTime = dateRange.startDate.toISOString();
    const endTime = dateRange.endDate.toISOString();
    const comparisonStartTime = dateRange.previousStartDate?.toISOString() ?? null;
    const comparisonEndTime = dateRange.previousEndDate?.toISOString() ?? null;
    const params = {
      startTime,
      endTime,
      comparisonStartTime,
      comparisonEndTime,
      queueMicrosoftIds: null,
      agentMicrosoftIds: null,
    };
    setScorecardLoading(true);
    setScorecardPayload(null);
    Promise.all([
      fetchCallQualityDistribution(params, { signal }),
      fetchCallQualityMetrics(params, { signal }),
    ])
      .then(([distribution, metrics]) => {
        const combined: CallQualityScorecardResponse = {
          distribution,
          kpiCards: metrics.kpiCards,
          failureCallIds: metrics.failureCallIds,
          callSetupIds: metrics.callSetupIds,
          callDropIds: metrics.callDropIds,
        };
        setScorecardPayload(combined);
      })
      .catch((err) => {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.error("Call Quality fetch failed:", err);
        }
      })
      .finally(() => setScorecardLoading(false));
    return () => controller.abort();
  }, [dateRange.startDate, dateRange.endDate, dateRange.previousStartDate, dateRange.previousEndDate]);

  const updateDateRange = (value: DateRangeFilterChange) => {
    setDateRange(value);
    setSharedDateRange(value);
    setCallMicrosoftIdsFilter(undefined);
    setQualityLevelFilter(undefined);
    setFailureTypeContext(undefined);
  };

  const scrollToCallQualityDetail = useCallback(() => {
    setTimeout(() => {
      if (callQualityDetailRef.current) {
        callQualityDetailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  }, []);

  const handleSetupFailedClick = useCallback((callMicrosoftIds: string[]) => {
    setCallMicrosoftIdsFilter(callMicrosoftIds.length > 0 ? callMicrosoftIds : undefined);
    setQualityLevelFilter(undefined);
    setFailureTypeContext("setup");
    scrollToCallQualityDetail();
  }, [scrollToCallQualityDetail]);

  const handleDropFailedClick = useCallback((callMicrosoftIds: string[]) => {
    setCallMicrosoftIdsFilter(callMicrosoftIds.length > 0 ? callMicrosoftIds : undefined);
    setQualityLevelFilter(undefined);
    setFailureTypeContext("drop");
    scrollToCallQualityDetail();
  }, [scrollToCallQualityDetail]);

  const handleQualityDistributionClick = useCallback((quality: "Good" | "Poor" | "Unclassified") => {
    setQualityLevelFilter(quality);
    setCallMicrosoftIdsFilter(undefined);
    setFailureTypeContext(undefined);
    scrollToCallQualityDetail();
  }, [scrollToCallQualityDetail]);

  if (!isGeneralMode) return null;

  return (
    <div className="w-full">
      <div className="border text-primary bg-[#FCFCFD] rounded-lg dark:border-[#3C5C73] dark:bg-[#3C5C73] dark:text-white" style={{ padding: "8px" }}>
        <div className="mb-4 flex flex-col md:flex-row align-center gap-2">
          <DateRangeFilter
            value={dateRange.key}
            onChange={updateDateRange}
            initialDateRange={dateRange as unknown as DateRangeChange}
          />
        </div>

        <CallQualityMetricsSection
          scorecardPayload={scorecardPayload}
          loading={scorecardLoading}
          onSetupFailedClick={handleSetupFailedClick}
          onDropFailedClick={handleDropFailedClick}
          onQualityDistributionClick={handleQualityDistributionClick}
        />

        <div ref={callQualityDetailRef}>
          <CallQualityDetailWidget
            dateParams={callQualityDetailDateParams}
            title="Call Quality Detail"
            limit={20}
            qualityLevelFilter={qualityLevelFilter}
            onClearExternalFilters={() => {
              setQualityLevelFilter(undefined);
              setCallMicrosoftIdsFilter(undefined);
              setFailureTypeContext(undefined);
            }}
            onViewDetails={(row) => {
              setSelectedRow(row);
              setDetailsModalOpen(true);
            }}
          />
        </div>

        <CallQualityDetailsModal
          open={detailsModalOpen}
          onOpenChange={setDetailsModalOpen}
          row={selectedRow}
          failureTypeContext={failureTypeContext}
        />

      </div>
    </div>
  );
};

export default CQDTab;
